import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { Box, Link, NoSsr, Typography } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'

import { MIN_BOX_BOOK_NUMBER_OF_PAGES } from 'common/utils/bookSize.constants'
import { formatPrice } from 'common/utils/format'
import Preview from 'artkive/components/Modals/Preview'
import ProductDescriptionItem from 'artkive/components/PageSections/ProductDescriptionItem'
import { getMosaicSlide } from 'artkive/components/sections/YouWillAlsoLoveCarousel'
import { TrackButton } from 'artkive/components/Tracker'
import usePageScrollTracker from 'artkive/components/Tracker/usePageScrollTracker'
import VirtualGalleryCountdownBox from 'artkive/components/VirtualGalleryCountdownBox'
import { ART_BOOK, GET_BOX_CLICK } from 'artkive/constants/tracker/mainNavigation'
import withApp from 'artkive/hoc/withApp'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import useSale from 'artkive/hooks/useSale'
import useScrollToSlider from 'artkive/hooks/useScrollToSlider'
import {
  BeforeAfterSection,
  BoxDueDisclaimer,
  CtaButton,
  FeatureQuoteSection,
  HeroDivider,
  HeroDividerWrapper,
  HeroTitle,
  ImageCardRow,
  KeepsakeDigitalStorage,
  KeepsakeFaqs,
  KeepsakeGetStarted,
  KeepsakeHero,
  KeepsakeHowItWorks,
  KeepsakeSection,
  KeepsakeTestimonial,
  Phrases,
  ProductFeatures,
  RecipientCards,
} from 'artkive/pages/Keepsakes'
import { LimitedTextBox, LimitedTimeText } from 'artkive/pages/VirtualGallery/VirtualGallery.style'
import { HELP_EMAIL } from 'artkive/utils/company'
import { PRICING } from 'artkive/utils/constants'
import theme from 'artkive/utils/theme'

import ArtBooksHeroCrop from 'images/ecom/books/ArtBooks-hero-crop.jpg'
import ArtBooksHeroCropMobile from 'images/ecom/books/ArtBooks-hero-crop-mobile.jpg'
import ArtBooksHeroCropX2 from 'images/ecom/books/ArtBooks-hero-crop-x2.jpg'
import TestimonialImage from 'images/ecom/books/Testimonial.jpg'
import BrushStrokes from 'images/ecom/brand/brushStrokes.svg'
import Bubbles from 'images/ecom/bubbles.svg'

import {
  audienceCards,
  beforeAfter,
  bookProductBullets,
  FAQS,
  howItWorksSteps,
  imageBgPosition,
  keepsakeQuote,
  productFeatures,
  recipientGroups,
  testimonialUser,
  whoItsFor,
} from './Books.constants'
import {
  AlsoLove,
  CountdownBox,
  FeaturesBubbles,
  FeaturesSprite,
  FeaturesTitle,
  LookInsideBox,
  LookInsideButton,
  LookInsideSpaceBox,
  PriceInfoBox,
  PriceLabel,
  PricingText,
  SliderBox,
  StartingPrice,
  TestimonialSection,
  Wrapper,
} from './Books.style'
import LookInsidePreview from './LookInsidePreview'
import PriceSlider from './PriceSlider'

const Books = ({ pricing, mosaic_pricing }) => {
  usePageScrollTracker()
  const [sliderValue, setSliderValue] = useState(MIN_BOX_BOOK_NUMBER_OF_PAGES)
  const [isOpen, setIsOpen] = useState(false)
  const sliderRef = useScrollToSlider()
  const { isSale, name, priceLabel, boxCheckoutUrl, countdownImageUrl } = useSale()

  const muiTheme = useTheme()

  const isMobile = useMobileBreakpoint()

  const slides = useMemo(() => [getMosaicSlide(mosaic_pricing)], [mosaic_pricing])

  const handleSliderChange = (e, value) => setSliderValue(value)

  return (
    <Wrapper>
      <KeepsakeHero
        image={
          <picture>
            <source srcSet={ArtBooksHeroCropMobile} media={muiTheme.breakpoints.down('sm').replace('@media ', '')} />
            <source srcSet={ArtBooksHeroCropX2} media={muiTheme.breakpoints.up('lg').replace('@media ', '')} />
            <img
              srcSet={`${ArtBooksHeroCrop}, ${ArtBooksHeroCropX2} 2x`}
              src={ArtBooksHeroCrop}
              alt='Kids Art Books preview'
              loading='eager'
            />
          </picture>
        }
        imageStyle='contain'
        productBg={theme.colors.bg.blue}
        overImage={
          <>
            <LookInsideBox>
              <LookInsideButton disableRipple variant='text' onClick={() => setIsOpen(true)}>
                Look <br />
                Inside!
              </LookInsideButton>
            </LookInsideBox>
            <LookInsideSpaceBox />
          </>
        }
      >
        <HeroTitle variant='h1' align={isMobile ? 'center' : 'left'}>
          <Phrases text='Kids Art Book: | Keepsake Edition' />
        </HeroTitle>
        <Box mb={3} mt={2}>
          <Typography variant='body1'>
            Every crayon handprint and macaroni masterpiece, bound into one real book, not a folder, not a file. The
            story of your kids' childhood, ready to open again in twenty years.
          </Typography>
        </Box>
        {isMobile && (
          <HeroDividerWrapper>
            <HeroDivider />
          </HeroDividerWrapper>
        )}
        {bookProductBullets.map(({ text }, index) => (
          <ProductDescriptionItem key={index}>{text}</ProductDescriptionItem>
        ))}
        <PriceInfoBox>
          <Typography variant='body1'>
            <strong>
              Starting at <StartingPrice>{formatPrice(PRICING.books.base, 'short')}</StartingPrice> for a{' '}
              {PRICING.books.minPages}-page Book
            </strong>
          </Typography>
          <Typography variant='caption'>*Artkive Box sold separately</Typography>
        </PriceInfoBox>
        {isMobile && (
          <Box display='flex' alignItems='center' justifyContent='center' mt={5} mb={isSale ? 6 : 0}>
            <TrackButton
              color='primary'
              href={boxCheckoutUrl}
              size='large'
              variant='contained'
              trackerEvent={GET_BOX_CLICK}
              trackerOptions={{
                section: 'product_description_section',
              }}
            >
              {isSale ? `Get My ${priceLabel} Box` : 'Get My Box'}
            </TrackButton>
          </Box>
        )}
        {isSale ? (
          <>
            {!isMobile && (
              <LimitedTextBox>
                <LimitedTimeText>{name.toUpperCase()}</LimitedTimeText>
              </LimitedTextBox>
            )}
            <CountdownBox>
              <VirtualGalleryCountdownBox
                withImage
                title={`${priceLabel} ARTKIVE BOX`}
                description="Don't miss out - order now!"
                buttonLabel={`Get My ${priceLabel} Box`}
                buttonHref={boxCheckoutUrl}
                additionalText={name.toUpperCase()}
                imageUrl={countdownImageUrl}
              />
            </CountdownBox>
          </>
        ) : (
          !isMobile && (
            <Box display='flex' justifyContent='flex-start' mt={4}>
              <TrackButton
                color='primary'
                href={boxCheckoutUrl}
                size='large'
                variant='contained'
                trackerEvent={GET_BOX_CLICK}
                trackerOptions={{
                  section: 'product_description_section',
                }}
              >
                Get My Box
              </TrackButton>
            </Box>
          )
        )}
      </KeepsakeHero>
      <ImageCardRow {...audienceCards} data-testid='audience-cards' />
      <BeforeAfterSection {...beforeAfter} data-testid='before-after' />
      <KeepsakeSection bgcolor={isMobile ? theme.colors.sky[300] : 'transparent'}>
        <Typography variant='h2' align='center' id='slider' ref={sliderRef}>
          <Phrases text='Kids Art Book | Pricing Slider' />
        </Typography>
        <PricingText textAlign={isMobile ? 'left' : 'center'} mt={3} mb={3}>
          <Typography variant='body1'>
            The cost of your book is separate from your Artkive Box, and it depends on how many pieces of art you decide
            to send in your Box.
          </Typography>
        </PricingText>
        <PricingText textAlign={isMobile ? 'left' : 'center'} mb={4}>
          <Typography variant='body1'>
            For questions about pricing, please{' '}
            <strong>
              <Link href={`mailto:${HELP_EMAIL}`}>Contact Us.</Link>
            </strong>
          </Typography>
        </PricingText>
        <Box display='flex' justifyContent='center' mt={isMobile ? 5 : 6}>
          <PriceLabel value={sliderValue} pricing={pricing} />
        </Box>
        <SliderBox>
          <PriceSlider value={sliderValue} onChange={handleSliderChange} />
        </SliderBox>
        <Box textAlign='center'>
          <CtaButton
            color='primary'
            href={boxCheckoutUrl}
            size='large'
            variant='contained'
            trackerEvent={GET_BOX_CLICK}
            trackerOptions={{
              section: 'books_price_slider',
            }}
          >
            Shop Artkive Box
          </CtaButton>
        </Box>
      </KeepsakeSection>
      <TestimonialSection>
        <KeepsakeTestimonial
          imageSrc={TestimonialImage}
          imageBgPosition={imageBgPosition}
          quote="I sent in both of my kids artwork and the books and mosaics artkive created were simply stunning. My kids
                art is preserved forever and I don't have boxes to store anymore."
          user={testimonialUser}
        />
      </TestimonialSection>
      <div id='preview'></div>
      <LookInsidePreview />
      <KeepsakeHowItWorks
        id='how-it-works'
        title='How To Make | Your Keepsake'
        subtitle='From pile to book in four steps'
        steps={howItWorksSteps}
      >
        <Box justifyContent='center' display='flex' pt={6}>
          <CtaButton
            color='primary'
            href={boxCheckoutUrl}
            size='large'
            variant='contained'
            trackerEvent={GET_BOX_CLICK}
            trackerOptions={{ section: 'how_it_works' }}
          >
            Get My Box
          </CtaButton>
        </Box>
      </KeepsakeHowItWorks>
      <BoxDueDisclaimer />
      <ProductFeatures
        title={
          <FeaturesTitle variant='h2' align='center'>
            <Phrases text='There’s So Much Inside | (And Outside, Too)' />
          </FeaturesTitle>
        }
        features={productFeatures}
      >
        {isMobile && (
          <>
            <FeaturesSprite src={BrushStrokes} alt='' />
            <FeaturesBubbles src={Bubbles} alt='' />
          </>
        )}
      </ProductFeatures>
      <FeatureQuoteSection {...keepsakeQuote} data-testid='keepsake-quote' />
      <RecipientCards {...whoItsFor} groups={recipientGroups} data-testid='who-its-for' />
      <KeepsakeDigitalStorage mobileBottomSpacing={theme.spacings.sectionMobile} />
      <KeepsakeGetStarted />
      <KeepsakeFaqs faqs={FAQS} trackerEvent={ART_BOOK.FAQ_ITEM_OPEN} />
      <AlsoLove slides={slides} />
      <NoSsr>
        <Preview
          maxWidth={isMobile ? false : 'xl'}
          fullScreen={isMobile}
          fullWidth
          open={isOpen}
          onClose={() => setIsOpen(false)}
        />
      </NoSsr>
    </Wrapper>
  )
}

Books.propTypes = {
  pricing: PropTypes.object.isRequired,
  mosaic_pricing: PropTypes.shape({
    small: PropTypes.object.isRequired,
    large: PropTypes.object.isRequired,
  }).isRequired,
}

export default withApp(Books)
